import { useRef, useState } from "react";
import { streamNdjson } from "../../api";
import { Spinner } from "../../components/Loaders";

type LogLevel = "info" | "muted" | "ok" | "warn" | "err";
interface LogRow { text: string; level: LogLevel; id?: string }

function fmtBytes(n: number | undefined | null): string {
  if (!n || n <= 0) return "0 B";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let i = 0;
  let f = Number(n);
  while (f >= 1024 && i < units.length - 1) {
    f /= 1024;
    i += 1;
  }
  return `${f.toFixed(1)} ${units[i]}`;
}

export function AdminUpdate() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [running, setRunning] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);

  const append = (text: string, level: LogLevel = "info") => {
    setRows((r) => [...r, { text, level }]);
  };

  const upsertProgress = (id: string, text: string) => {
    setRows((r) => {
      const i = r.findIndex((x) => x.id === id);
      if (i < 0) return [...r, { text, level: "muted", id }];
      const next = [...r];
      next[i] = { ...next[i], text };
      return next;
    });
  };

  const handle = (ev: any) => {
    switch (ev.type) {
      case "manifest_fetched":
        append(`Card data manifest: ${ev.count} bulk entries.`, "muted");
        break;
      case "check":
        if (ev.decision === "up_to_date") {
          append(`  ${ev.bulk_type}: already current (${ev.updated_at}).`, "ok");
        } else if (ev.decision === "needs_download") {
          append(
            `  ${ev.bulk_type}: new dump (${ev.remote_updated_at}, ${fmtBytes(ev.size)}).`,
            "info",
          );
        } else if (ev.decision === "skipped") {
          append(`  ${ev.bulk_type}: skipped (${ev.reason}).`, "warn");
        }
        break;
      case "download_start":
        append(`Downloading ${ev.bulk_type} (${fmtBytes(ev.size)})...`, "info");
        break;
      case "download_progress": {
        const total = ev.total || 0;
        const done = ev.bytes || 0;
        const pct = total ? ((100 * done) / total).toFixed(1) + "%" : "";
        upsertProgress(
          `dl-${ev.bulk_type}`,
          `  ${ev.bulk_type}: ${fmtBytes(done)} / ${fmtBytes(total)} ${pct}`,
        );
        break;
      }
      case "download_done":
        append(`  ${ev.bulk_type}: downloaded ${fmtBytes(ev.bytes)}`, "ok");
        break;
      case "cleanup":
        (ev.removed || []).forEach((p: string) =>
          append(`  removed old file: ${p}`, "muted"),
        );
        break;
      case "summary":
        append("Sync summary:", "info");
        (ev.results || []).forEach((r: any) =>
          append(`  ${r.type}: ${r.action}${r.path ? " (" + r.path + ")" : ""}`, "muted"),
        );
        break;
      case "preprocess_skipped":
        append(`Skipping preprocess: ${ev.reason}.`, "muted");
        break;
      case "preprocess_start":
        append(`Rebuilding ${ev.db_path}. This may take a few minutes...`, "info");
        break;
      case "preprocess_done":
        append("Preprocess complete. cards.db is now up to date.", "ok");
        break;
      case "done":
        append(`All done.${ev.preprocessed ? "" : " (DB was not rebuilt.)"}`, "ok");
        break;
      case "error":
        append(ev.message || "Unknown error.", "err");
        break;
      default:
        append(JSON.stringify(ev), "muted");
    }
  };

  const startUpdate = async () => {
    setRows([]);
    setRunning(true);
    append("Checking for newer card data bulk files...", "muted");
    const { promise, abort } = streamNdjson<any>({
      path: "/api/update",
      body: { types: ["all_cards", "rulings"], preprocess: true },
      onEvent: handle,
      onError: (m) => append(`Error: ${m}`, "err"),
    });
    abortRef.current = abort;
    try {
      await promise;
    } catch {
      /* already logged */
    } finally {
      abortRef.current = null;
      setRunning(false);
    }
  };

  const cancel = () => abortRef.current?.();

  return (
    <>
      <h2>Card data update</h2>
      <p className="muted">
        Downloads the newest card data bulk dump and rebuilds <code>cards.db</code>.
        Only re-downloads when a newer file is published, so
        this is safe to run any time.
      </p>

      <div className="actions">
        <button type="button" className="primary" onClick={startUpdate} disabled={running}>
          {running ? (
            <>
              <Spinner className="button-spinner" />
              Updating...
            </>
          ) : (
            "Run update now"
          )}
        </button>
        <button className="secondary" onClick={cancel} disabled={!running}>
          Cancel
        </button>
      </div>

      {rows.length > 0 && (
        <div className="update-log">
          {rows.map((r, i) => (
            <span key={i} className={`row ${r.level}`}>{r.text}</span>
          ))}
        </div>
      )}

      <div className="rationale-block">
        <h3>Schedule the same work (optional)</h3>
        <p>
          The button above calls the app API. For a long-running install you can
          also run the same sync from the host with{" "}
          <code>python -m backend.update_cards</code> (from the project root, with
          the venv active). Helper scripts log to <code>data/update.log</code>{" "}
          (the file is trimmed when it grows past about 2&nbsp;MB).
        </p>

        <h4 className="admin-subhead">Linux / macOS (cron, Ubuntu server, etc.)</h4>
        <p>
          Use <code>scripts/update_daily.sh</code> (make it executable:{" "}
          <code>chmod +x scripts/update_daily.sh</code>). Example{" "}
          <code>crontab -e</code> entry (adjust the path to your checkout):
        </p>
        <pre className="arena-text">{`15 2 * * * /opt/foilbloom/app/scripts/update_daily.sh >>/opt/foilbloom/app/data/cron-update.log 2>&1`}</pre>
        <p className="hint">
          For systemd instead of cron, use a <code>oneshot</code> service that
          runs that script, plus a <code>.timer</code> unit for the schedule.
        </p>

        <h4 className="admin-subhead">Windows</h4>
        <p>
          Use Task Scheduler to run <code>scripts\\update_daily.ps1</code>. A
          typical action:
        </p>
        <pre className="arena-text">{`Program:  powershell.exe
Args:     -NoProfile -ExecutionPolicy Bypass -File "C:\\path\\to\\foilbloom.com\\scripts\\update_daily.ps1"
Start in: C:\\path\\to\\foilbloom.com`}</pre>
      </div>
    </>
  );
}
